import React, { useState } from 'react';
import { X, Mail, Phone, Instagram, Building2, MapPin, CreditCard, Send, ChevronDown, ChevronRight } from 'lucide-react';
import { contactsAPI } from '../services/api';
import { toast } from 'react-toastify';

const StatRing = ({ value, max, label, color, darkMode }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  const r = 28, c = 2 * Math.PI * r, offset = c - (pct / 100) * c;
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-16 h-16">
        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r={r} fill="none" stroke={darkMode ? '#27272a' : '#f3f4f6'} strokeWidth="4" />
          <circle cx="32" cy="32" r={r} fill="none" stroke={color} strokeWidth="4"
            strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-700" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{value}</span>
        </div>
      </div>
      <span className={`text-[10px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{label}</span>
    </div>
  );
};

const Section = ({ title, icon, count, children, darkMode, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const border = darkMode ? 'border-zinc-700' : 'border-gray-100';
  const t2 = darkMode ? 'text-gray-400' : 'text-gray-500';
  return (
    <div className={`border-b last:border-0 ${border}`}>
      <button onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between py-3 px-1 text-left transition-colors ${darkMode ? 'hover:bg-zinc-800/50' : 'hover:bg-gray-50/50'}`}>
        <div className="flex items-center gap-2">
          <span className="text-sm">{icon}</span>
          <span className={`text-xs font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</span>
          {count != null && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-zinc-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{count}</span>}
        </div>
        {open ? <ChevronDown size={14} className={t2} /> : <ChevronRight size={14} className={t2} />}
      </button>
      {open && <div className="pb-3 px-1">{children}</div>}
    </div>
  );
};

export default function ContactModal({ contact, isNew, darkMode, onClose, onSave, user }) {
  const [tab, setTab] = useState(isNew ? 'dados' : 'resumo');
  const [details, setDetails] = useState(contact || null);
  const [loading, setLoading] = useState(!isNew);
  const [showAllCampanhas, setShowAllCampanhas] = useState(false);
  const [showAllSegmentos, setShowAllSegmentos] = useState(false);
  const [showAllAtendimento, setShowAllAtendimento] = useState(false);
  const [showAddCanal, setShowAddCanal] = useState(false);
  const [novoCanal, setNovoCanal] = useState({ plataforma: 'whatsapp', identificador: '' });
  const [form, setForm] = useState({
    nome: contact?.nome || '', telefone: contact?.telefone || '',
    email: contact?.email || '', instagram: contact?.instagram || '',
    facebook: contact?.facebook || '', empresa: contact?.empresa || '',
    cargo: contact?.cargo || '', endereco: contact?.endereco || '',
    cpf_cnpj: contact?.cpf_cnpj || '', status: contact?.status !== false,
  });

  React.useEffect(() => {
    if (!isNew && contact?.id) {
      contactsAPI.getOne(user.cliente_id, contact.id).then(r => {
        setDetails(r.data);
        setForm(f => ({
          ...f, nome: r.data.nome || '', telefone: r.data.telefone || '',
          email: r.data.email || '', instagram: r.data.instagram || '',
          facebook: r.data.facebook || '', empresa: r.data.empresa || '',
          cargo: r.data.cargo || '', endereco: r.data.endereco || '',
          cpf_cnpj: r.data.cpf_cnpj || '', status: r.data.status !== false,
        }));
        setLoading(false);
      }).catch(() => { toast.error('Erro ao carregar'); onClose(); });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isNew) {
        await contactsAPI.create({ ...form, cliente_id: user.cliente_id, usuario_id: user.id });
        toast.success('Contato criado');
      } else {
        await contactsAPI.update(user.cliente_id, contact.id, form);
        toast.success('Contato atualizado');
      }
      onSave(); onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Erro ao salvar'); }
  };

  const addComment = async (text) => {
    const token = localStorage.getItem('token');
    await fetch(`${process.env.REACT_APP_API_URL || ''}/contatos/${contact.id}/comentarios`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ comentario: text }),
    });
    const r = await contactsAPI.getOne(user.cliente_id, contact.id);
    setDetails(r.data);
  };

  const refreshDetails = async () => {
    const r = await contactsAPI.getOne(user.cliente_id, contact.id);
    setDetails(r.data);
  };

  const handleAddCanal = async (e) => {
    e.preventDefault();
    if (!novoCanal.identificador.trim()) return;
    try {
      await contactsAPI.addCanal(contact.id, novoCanal);
      await refreshDetails();
      setNovoCanal({ plataforma: 'whatsapp', identificador: '' });
      setShowAddCanal(false);
      toast.success('Canal adicionado');
    } catch { toast.error('Erro ao adicionar canal'); }
  };

  const handleRemoveCanal = async (canalId) => {
    try {
      await contactsAPI.removeCanal(canalId);
      await refreshDetails();
      toast.success('Canal removido');
    } catch { toast.error('Erro ao remover'); }
  };

  const stats = details?.estatisticas;
  const msgs = stats?.mensagens || {};
  const atd = stats?.atendimento || {};
  const totalMsgs = parseInt(atd.total || 0);
  const bg = darkMode ? 'bg-zinc-900' : 'bg-white';
  const cardBg = darkMode ? 'bg-zinc-800' : 'bg-gray-50';
  const border = darkMode ? 'border-zinc-700' : 'border-gray-100';
  const t1 = darkMode ? 'text-white' : 'text-gray-900';
  const t2 = darkMode ? 'text-gray-400' : 'text-gray-500';
  const t3 = darkMode ? 'text-gray-600' : 'text-gray-300';
  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-nomoz-orange/50 focus:border-nomoz-orange ${darkMode ? 'border-zinc-700 bg-zinc-800 text-white placeholder-zinc-600' : 'border-gray-200 bg-white text-gray-900 placeholder-gray-300'}`;

  const tabs = isNew ? [{ id: 'dados', label: 'Dados' }] : [
    { id: 'resumo', label: 'Resumo' },
    { id: 'dados', label: 'Editar' },
    { id: 'notas', label: `Notas${details?.comentarios?.length ? ` (${details.comentarios.length})` : ''}` },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className={`w-full max-w-2xl ${bg} rounded-2xl shadow-2xl flex flex-col overflow-hidden mt-8 mb-8`}
        style={{ maxHeight: 'calc(100vh - 4rem)' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="px-4 sm:px-6 pt-5 pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className={`h-14 w-14 rounded-full flex items-center justify-center overflow-hidden ${
                form.status ? 'bg-gradient-to-br from-gray-200 to-gray-300' : 'bg-gray-400'
              }`}>
                <svg className={`h-10 w-10 translate-y-1 ${form.status ? 'text-gray-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
                </svg>
              </div>
              <div>
                <h2 className={`text-lg font-semibold ${t1}`}>{isNew ? 'Novo Contato' : form.nome}</h2>
                {!isNew && (
                  <div className="flex items-center gap-4 mt-1">
                    {form.telefone && <span className={`text-xs flex items-center gap-1 ${t2}`}><Phone size={12} />{form.telefone}</span>}
                    {form.email && <span className={`text-xs flex items-center gap-1 ${t2}`}><Mail size={12} />{form.email}</span>}
                    {form.empresa && <span className={`text-xs flex items-center gap-1 ${t2}`}><Building2 size={12} />{form.empresa}{form.cargo ? ` · ${form.cargo}` : ''}</span>}
                  </div>
                )}
                {!isNew && (form.instagram || form.facebook || form.endereco) && (
                  <div className="flex items-center gap-4 mt-0.5">
                    {form.instagram && <span className={`text-xs flex items-center gap-1 ${t2}`}><Instagram size={12} />{form.instagram}</span>}
                    {form.facebook && <span className={`text-xs flex items-center gap-1 ${t2}`}>📘 {form.facebook}</span>}
                    {form.endereco && <span className={`text-xs flex items-center gap-1 ${t2}`}><MapPin size={12} />{form.endereco}</span>}
                  </div>
                )}
              </div>
            </div>
            <button onClick={onClose} className={`p-1.5 rounded-full transition-colors ${darkMode ? 'hover:bg-zinc-800 text-gray-500' : 'hover:bg-gray-100 text-gray-400'}`}>
              <X size={18} />
            </button>
          </div>
          <div className="flex gap-1 mt-4">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  tab === t.id ? 'bg-nomoz-orange text-white shadow-sm'
                    : darkMode ? 'text-gray-500 hover:text-gray-300 hover:bg-zinc-800' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                }`}>{t.label}</button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className={`flex-1 overflow-y-auto px-4 sm:px-6 pb-6 border-t ${border}`}>
          {loading && !isNew ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-nomoz-orange border-t-transparent" />
            </div>

          ) : tab === 'resumo' ? (
            <div className="pt-4 space-y-4">
              {/* Atividade */}
              <div className={`p-4 rounded-xl ${cardBg}`}>
                <p className={`text-[10px] uppercase tracking-wider font-semibold mb-3 ${t3}`}>Atividade</p>
                <div className="flex items-center justify-around">
                  <StatRing value={parseInt(atd.total || 0)} max={totalMsgs || 1} label="Conversas" color="#f97316" darkMode={darkMode} />
                  <StatRing value={parseInt(atd.recebidas || 0)} max={totalMsgs || 1} label="Recebidas" color="#22c55e" darkMode={darkMode} />
                  <StatRing value={parseInt(atd.enviadas || 0)} max={totalMsgs || 1} label="Enviadas" color="#3b82f6" darkMode={darkMode} />
                  <StatRing value={parseInt(msgs.lidas || 0)} max={parseInt(msgs.total || 1)} label="Lidas" color="#a855f7" darkMode={darkMode} />
                </div>
                {atd.primeira_mensagem && (
                  <div className={`flex justify-between mt-3 pt-3 border-t ${border}`}>
                    <span className={`text-[10px] ${t3}`}>Primeira: {new Date(atd.primeira_mensagem).toLocaleDateString('pt-BR')}</span>
                    <span className={`text-[10px] ${t3}`}>Última: {new Date(atd.ultima_mensagem).toLocaleDateString('pt-BR')}</span>
                  </div>
                )}
              </div>

              {/* Campanhas — 2 visíveis + ver tudo */}
              {/* Canais */}
              {details?.canais?.length > 0 && (
                <div>
                  <p className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${t3}`}>🔗 Canais ({details.canais.length})</p>
                  <div className="space-y-1.5">
                    {details.canais.map(c => (
                      <div key={c.id} className={`flex items-center gap-2 p-2 rounded-lg ${cardBg}`}>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          c.plataforma === 'whatsapp' ? 'bg-emerald-100 text-emerald-700' :
                          c.plataforma === 'instagram' ? 'bg-pink-100 text-pink-700' :
                          c.plataforma === 'facebook' ? 'bg-blue-100 text-blue-700' :
                          c.plataforma === 'email' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'
                        }`}>{c.plataforma}</span>
                        <span className={`text-xs ${t1}`}>{c.identificador}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Campanhas — 2 visíveis + ver tudo */}
              {stats?.campanhas?.length > 0 && (
                <div>
                  <p className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${t3}`}>📣 Campanhas ({stats.campanhas.length})</p>
                  <div className="space-y-2">
                    {(showAllCampanhas ? stats.campanhas : stats.campanhas.slice(0, 2)).map(c => (
                      <div key={c.id} className={`p-3 rounded-xl ${cardBg} flex items-center justify-between`}>
                        <p className={`text-xs font-medium ${t1}`}>{c.nome}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                          c.status_execucao === 'completa' ? 'bg-emerald-100 text-emerald-700' :
                          c.status_execucao === 'erro' ? 'bg-red-100 text-red-700' :
                          c.status_execucao === 'incompleta' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                        }`}>{c.status_execucao === 'completa' ? 'Concluída' : c.status_execucao === 'erro' ? 'Erro' : c.status_execucao === 'incompleta' ? 'Incompleta' : 'Pendente'}</span>
                      </div>
                    ))}
                  </div>
                  {stats.campanhas.length > 2 && (
                    <button onClick={() => setShowAllCampanhas(!showAllCampanhas)} className={`text-xs mt-2 ${darkMode ? 'text-nomoz-orange' : 'text-nomoz-orange'} hover:underline`}>
                      {showAllCampanhas ? 'Ver menos' : `Ver todas (${stats.campanhas.length})`}
                    </button>
                  )}
                </div>
              )}

              {/* Segmentos — 2 visíveis + ver tudo */}
              {stats?.grupos?.length > 0 && (
                <div>
                  <p className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${t3}`}>👥 Segmentos ({stats.grupos.length})</p>
                  <div className="space-y-2">
                    {(showAllSegmentos ? stats.grupos : stats.grupos.slice(0, 2)).map(g => (
                      <div key={g.id} className={`p-3 rounded-xl ${cardBg} flex items-center justify-between`}>
                        <p className={`text-xs font-medium ${t1}`}>{g.nome}</p>
                        <span className={`text-[10px] ${t2}`}>{g.total_contatos || 0} contatos</span>
                      </div>
                    ))}
                  </div>
                  {stats.grupos.length > 2 && (
                    <button onClick={() => setShowAllSegmentos(!showAllSegmentos)} className="text-xs mt-2 text-nomoz-orange hover:underline">
                      {showAllSegmentos ? 'Ver menos' : `Ver todos (${stats.grupos.length})`}
                    </button>
                  )}
                </div>
              )}

              {/* Histórico de Atendimento — 3 visíveis + ver tudo */}
              {stats?.historicoTags?.length > 0 && (
                <div>
                  <p className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${t3}`}>📋 Histórico de Atendimento ({stats.historicoTags.length})</p>
                  <div className="space-y-1">
                    {(showAllAtendimento ? stats.historicoTags : stats.historicoTags.slice(0, 3)).map((h, i) => (
                      <div key={i} className="flex items-center gap-3 py-1.5">
                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${h.motivo === 'finalizar_atendimento' ? 'bg-gray-400' : h.acao === 'removida' ? 'bg-red-400' : 'bg-emerald-400'}`} />
                        <span className="text-[10px] px-2 py-0.5 rounded-full text-white font-medium flex-shrink-0" style={{ backgroundColor: h.tag_cor || '#6b7280' }}>{h.tag_nome}</span>
                        <span className={`text-[10px] ${t2}`}>{h.motivo === 'finalizar_atendimento' ? 'Finalizado' : h.acao === 'removida' ? 'Removida' : 'Iniciado'}</span>
                        <span className={`text-[10px] ml-auto ${t3}`}>{new Date(h.data_acao).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    ))}
                  </div>
                  {stats.historicoTags.length > 3 && (
                    <button onClick={() => setShowAllAtendimento(!showAllAtendimento)} className="text-xs mt-2 text-nomoz-orange hover:underline">
                      {showAllAtendimento ? 'Ver menos' : `Ver tudo (${stats.historicoTags.length})`}
                    </button>
                  )}
                </div>
              )}

              {totalMsgs === 0 && !stats?.campanhas?.length && !stats?.grupos?.length && !stats?.historicoTags?.length && (
                <div className="text-center py-8">
                  <p className="text-3xl mb-2">📭</p>
                  <p className={`text-sm ${t2}`}>Nenhuma atividade ainda</p>
                </div>
              )}
            </div>

          ) : tab === 'dados' ? (
            <form id="contactForm" onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={`block text-[10px] uppercase tracking-wider font-semibold mb-1 ${t3}`}>Nome *</label>
                  <input required value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} placeholder="Nome completo" className={inputCls} />
                </div>
                <div>
                  <label className={`block text-[10px] uppercase tracking-wider font-semibold mb-1 ${t3}`}>Telefone *</label>
                  <input required value={form.telefone} onChange={e => setForm({ ...form, telefone: e.target.value.replace(/\D/g, '') })} placeholder="11999999999" className={inputCls} />
                </div>
              </div>
              <div className={`border-t pt-4 ${border}`}>
                <p className={`text-[10px] uppercase tracking-wider font-semibold mb-3 ${t3}`}>Redes & Contato</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="relative"><Mail size={14} className={`absolute left-3 top-2.5 ${t3}`} /><input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email" className={`${inputCls} pl-9`} /></div>
                  <div className="relative"><Instagram size={14} className={`absolute left-3 top-2.5 ${t3}`} /><input value={form.instagram} onChange={e => setForm({ ...form, instagram: e.target.value })} placeholder="@instagram" className={`${inputCls} pl-9`} /></div>
                  <div className="relative"><span className={`absolute left-3 top-2 text-sm ${t3}`}>f</span><input value={form.facebook} onChange={e => setForm({ ...form, facebook: e.target.value })} placeholder="Facebook" className={`${inputCls} pl-9`} /></div>
                  <div className="relative"><CreditCard size={14} className={`absolute left-3 top-2.5 ${t3}`} /><input value={form.cpf_cnpj} onChange={e => setForm({ ...form, cpf_cnpj: e.target.value })} placeholder="CPF/CNPJ" className={`${inputCls} pl-9`} /></div>
                </div>
              </div>
              <div className={`border-t pt-4 ${border}`}>
                <p className={`text-[10px] uppercase tracking-wider font-semibold mb-3 ${t3}`}>Profissional</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input value={form.empresa} onChange={e => setForm({ ...form, empresa: e.target.value })} placeholder="Empresa" className={inputCls} />
                  <input value={form.cargo} onChange={e => setForm({ ...form, cargo: e.target.value })} placeholder="Cargo" className={inputCls} />
                </div>
                <input value={form.endereco} onChange={e => setForm({ ...form, endereco: e.target.value })} placeholder="Endereço" className={`${inputCls} mt-3`} />
              </div>
              {!isNew && (
                <div className={`border-t pt-4 ${border}`}>
                  <div className="flex items-center justify-between mb-3">
                    <p className={`text-[10px] uppercase tracking-wider font-semibold ${t3}`}>Canais</p>
                    <button type="button" onClick={() => setShowAddCanal(!showAddCanal)} className="text-xs text-nomoz-orange hover:underline">
                      {showAddCanal ? 'Cancelar' : '+ Adicionar'}
                    </button>
                  </div>
                  {showAddCanal && (
                    <div className={`flex gap-2 mb-3 p-2 rounded-lg ${cardBg}`}>
                      <select value={novoCanal.plataforma} onChange={e => setNovoCanal({ ...novoCanal, plataforma: e.target.value })}
                        className={`rounded-lg border px-2 py-1.5 text-xs ${darkMode ? 'border-zinc-700 bg-zinc-800 text-white' : 'border-gray-200 bg-white text-gray-900'}`}>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="instagram">Instagram</option>
                        <option value="facebook">Facebook</option>
                        <option value="email">Email</option>
                        <option value="telegram">Telegram</option>
                      </select>
                      <input value={novoCanal.identificador} onChange={e => setNovoCanal({ ...novoCanal, identificador: e.target.value })}
                        placeholder="Número ou @usuario" className={`flex-1 rounded-lg border px-2 py-1.5 text-xs ${darkMode ? 'border-zinc-700 bg-zinc-800 text-white placeholder-zinc-600' : 'border-gray-200 bg-white text-gray-900'}`} />
                      <button type="button" onClick={handleAddCanal} className="px-3 py-1.5 bg-nomoz-orange text-white rounded-lg text-xs font-medium hover:opacity-90">Salvar</button>
                    </div>
                  )}
                  {details?.canais?.length > 0 && (
                    <div className="space-y-1.5">
                      {details.canais.map(c => (
                        <div key={c.id} className={`flex items-center justify-between p-2 rounded-lg ${cardBg}`}>
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                              c.plataforma === 'whatsapp' ? 'bg-emerald-100 text-emerald-700' :
                              c.plataforma === 'instagram' ? 'bg-pink-100 text-pink-700' :
                              c.plataforma === 'facebook' ? 'bg-blue-100 text-blue-700' :
                              c.plataforma === 'email' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'
                            }`}>{c.plataforma}</span>
                            <span className={`text-xs ${t1}`}>{c.identificador}</span>
                          </div>
                          <button type="button" onClick={() => handleRemoveCanal(c.id)} className={`p-1 rounded hover:bg-red-100 ${darkMode ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}>
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <div className={`w-9 h-5 rounded-full transition-colors relative ${form.status ? 'bg-emerald-500' : darkMode ? 'bg-zinc-700' : 'bg-gray-300'}`}
                    onClick={() => setForm({ ...form, status: !form.status })}>
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.status ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                  <span className={`text-xs ${t2}`}>{form.status ? 'Ativo' : 'Inativo'}</span>
                </label>
                <button type="submit" className="px-5 py-2 bg-nomoz-orange text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity shadow-sm">
                  {isNew ? 'Criar Contato' : 'Salvar'}
                </button>
              </div>
            </form>

          ) : tab === 'notas' ? (
            <div className="flex flex-col pt-4" style={{ minHeight: '200px' }}>
              <div className="flex-1 space-y-2 mb-4">
                {(details?.comentarios || []).length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-4xl mb-2">📝</p>
                    <p className={`text-sm ${t2}`}>Nenhuma nota ainda</p>
                  </div>
                )}
                {(details?.comentarios || []).map(c => (
                  <div key={c.id} className={`p-3 rounded-xl ${cardBg}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-nomoz-orange">{c.usuario_nome}</span>
                      <span className={`text-[10px] ${t3}`}>{new Date(c.create_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <p className={`text-xs leading-relaxed ${t2}`}>{c.comentario}</p>
                  </div>
                ))}
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const input = e.target.elements.nota;
                if (!input.value.trim()) return;
                try { await addComment(input.value.trim()); input.value = ''; } catch { toast.error('Erro ao salvar'); }
              }} className="flex gap-2">
                <input name="nota" placeholder="Adicionar nota..." className={`${inputCls} flex-1`} />
                <button type="submit" className="p-2 bg-nomoz-orange text-white rounded-lg hover:opacity-90 transition-opacity">
                  <Send size={16} />
                </button>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
